import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  User,
  ArrowLeft,
  Briefcase,
  Plus,
  X,
  Upload,
  Camera,
  Image as ImageIcon,
  MapPin,
  Mail,
  Phone,
  Award,
  Users,
  Info,
  ChevronRight,
  Edit,
  Save,
  CheckCircle,
  MessageCircle,
  Calendar,
  LogOut,
  Star,
  ChevronDown,
  ChevronUp,
  Lock,
  AlertTriangle,
} from "lucide-react";
import {
  getMarketplaceProfile,
  getPublicProfile,
  updateMarketplaceProfile,
  ProfileType,
  PortfolioItem,
  ProfessionalType,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { resolveImage } from "../../../Utils/helper";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import profileBgImg from "../../../assets/images/marketplace/Profile_bg.webp";
import MarketplaceLoader from "./MarketplaceLoader";
import { isProfileCompleted } from "./MarketplaceShared";
import {
  SidebarLayout,
  MainContentArea,
  ContentWrap,
  LoadingState,
  TitleCard,
  BackBtn,
  TitleBlock,
  PageTitle,
  PageSubtitle,
  HeaderActions,
  OutlineBtn,
  EditBtn,
  ProfileViewLayout,
  LeftCard,
  AvatarContainer,
  ProfilePicture,
  AvatarCircle,
  DisplayName,
  TypeBadge,
  VerifiedBadge,
  DetailsList,
  DetailItem,
  IconWrap,
  DetailContent,
  DetailLabel,
  DetailValue,
  RightCard,
  ViewSection,
  ViewSectionTitle,
  BioQuote,
  DetailsGrid,
  DetailGridCell,
  TagsWrapper,
  TagChip,
  PortfolioGrid,
  PortfolioLinkCard,
  FormCard,
  Section,
  SectionLabel,
  TwoCol,
  ThreeCol,
  FormGroup,
  Label,
  Input,
  Select,
  Textarea,
  ImageUploadAreaEdit,
  ImagePreviewEdit,
  PreviewImgEdit,
  RemoveImgBtnEdit,
  UploadLabelEdit,
  PortfolioList,
  PortfolioItemRow,
  RemovePortBtn,
  PortfolioAddBox,
  AddPortBtn,
  FormFooter,
  CancelBtn,
  SubmitBtn,
  PhotoModalOverlay,
  PhotoModalContent,
  CloseModalBtn,
  ModalTitle,
  OptionButton,
  CameraContainer,
  CameraVideo,
  CameraControls,
  CaptureBtn,
  PublicToggleArea,
  ToggleLabel,
  Toggle,
  ToggleKnob
} from "./MarketplaceMyProfilePageStyles";
import { MarketplaceReviewsSection } from "./MarketplaceReviewsSection";

export interface ProfileForm {
  display_name: string;
  bio: string;
  profile_picture: string;
  city: string;
  state: string;
  country: string;
  is_public: boolean;
  is_public_locked?: boolean;
  is_active?: boolean;
  work_categories: string;
  portfolio: PortfolioItem[];
  professional_type: ProfessionalType | "";
  profession: string;
  experience_years: string;
  skills: string;
  services_offered: string;
  team_size: string;
  business_info: string;
  contact_phone: string;
  contact_email: string;

  profile_avg_rating?: number;
  profile_review_count?: number;
  services_avg_rating?: number;
  services_review_count?: number;
  equipment_avg_rating?: number;
  equipment_review_count?: number;
}

const defaultForm: ProfileForm = {
  display_name: "",
  bio: "",
  profile_picture: "",
  city: "",
  state: "",
  country: "India",
  is_public: true,
  is_public_locked: false,
  is_active: true,
  work_categories: "",
  portfolio: [],
  professional_type: "",
  profession: "",
  experience_years: "",
  skills: "",
  services_offered: "",
  team_size: "",
  business_info: "",
  contact_phone: "",
  contact_email: "",
};

const csvToArray = (val: string) =>
  val.split(",").map((s) => s.trim()).filter(Boolean);

const MarketplaceMyProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [profile, setProfile] = useState<MarketplaceProfile | null>(null);
  const [ownProfile, setOwnProfile] = useState<MarketplaceProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<ProfileForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  const [showPhotoSourceModal, setShowPhotoSourceModal] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [newPortTitle, setNewPortTitle] = useState("");
  const [newPortUrl, setNewPortUrl] = useState("");

  // Determine if viewing someone else's profile
  const isOtherProfile = !!(id && ownProfile && String(ownProfile.id) !== String(id));

  const profileType: ProfileType = profile?.profile_type ?? "freelancer";
  const isFreelancer = profileType === "freelancer";
  const isStudio = profileType === "studio";

  const fetchProfile = async () => {
    setLoading(true);
    try {
      // First fetch current user's profile to check ownership
      let currentOwnProfile: MarketplaceProfile | null = null;
      try {
        const ownRes = await getMarketplaceProfile();
        if (ownRes?.data && isProfileCompleted(ownRes.data)) {
          currentOwnProfile = ownRes.data;
          setOwnProfile(ownRes.data);
          localStorage.setItem("marketplace_profile", JSON.stringify(ownRes.data));
        } else {
          setOwnProfile(null);
          localStorage.removeItem("marketplace_profile");
        }
      } catch (e) {
        setOwnProfile(null);
        localStorage.removeItem("marketplace_profile");
      }

      if (id && currentOwnProfile && String(currentOwnProfile.id) !== String(id)) {
        // Fetch public profile for id
        const pubRes = await getPublicProfile(Number(id));
        if (pubRes?.data) {
          setProfile(pubRes.data);
          const p = pubRes.data;
          setForm({
            display_name: p.display_name ?? "",
            bio: p.bio ?? "",
            profile_picture: p.profile_picture ?? "",
            city: p.city ?? "",
            state: p.state ?? "",
            country: p.country ?? "India",
            is_public: p.is_public ?? true,
            work_categories: (p.work_categories ?? []).join(", "),
            portfolio: p.portfolio ?? [],
            professional_type: p.professional_type ?? "",
            profession: p.profession ?? "",
            experience_years: p.experience_years != null ? String(p.experience_years) : "",
            skills: (p.skills ?? []).join(", "),
            services_offered: (p.services_offered ?? []).join(", "),
            team_size: p.team_size != null ? String(p.team_size) : "",
            business_info: p.business_info ?? "",
            contact_phone: p.contact_phone ?? "",
            contact_email: p.contact_email ?? "",
            profile_avg_rating: p.profile_avg_rating,
            profile_review_count: p.profile_review_count,
            services_avg_rating: p.services_avg_rating,
            services_review_count: p.services_review_count,
            equipment_avg_rating: p.equipment_avg_rating,
            equipment_review_count: p.equipment_review_count,
          });
        }
      } else if (id && !currentOwnProfile) {
        const pubRes = await getPublicProfile(Number(id));
        if (pubRes?.data) {
          setProfile(pubRes.data);
          const p = pubRes.data;
          setForm({
            display_name: p.display_name ?? "",
            bio: p.bio ?? "",
            profile_picture: p.profile_picture ?? "",
            city: p.city ?? "",
            state: p.state ?? "",
            country: p.country ?? "India",
            is_public: p.is_public ?? true,
            work_categories: (p.work_categories ?? []).join(", "),
            portfolio: p.portfolio ?? [],
            professional_type: p.professional_type ?? "",
            profession: p.profession ?? "",
            experience_years: p.experience_years != null ? String(p.experience_years) : "",
            skills: (p.skills ?? []).join(", "),
            services_offered: (p.services_offered ?? []).join(", "),
            team_size: p.team_size != null ? String(p.team_size) : "",
            business_info: p.business_info ?? "",
            contact_phone: p.contact_phone ?? "",
            contact_email: p.contact_email ?? "",
            profile_avg_rating: p.profile_avg_rating,
            profile_review_count: p.profile_review_count,
            services_avg_rating: p.services_avg_rating,
            services_review_count: p.services_review_count,
            equipment_avg_rating: p.equipment_avg_rating,
            equipment_review_count: p.equipment_review_count,
          });
        }
      } else {
        if (currentOwnProfile && isProfileCompleted(currentOwnProfile)) {
          setProfile(currentOwnProfile);
          const p = currentOwnProfile;
          setForm({
            display_name: p.display_name ?? "",
            bio: p.bio ?? "",
            profile_picture: p.profile_picture ?? "",
            city: p.city ?? "",
            state: p.state ?? "",
            country: p.country ?? "India",
            is_public: p.is_public ?? true,
            is_public_locked: Boolean(p.is_public_locked),
            is_active: p.is_active ?? true,
            work_categories: (p.work_categories ?? []).join(", "),
            portfolio: p.portfolio ?? [],
            professional_type: p.professional_type ?? "",
            profession: p.profession ?? "",
            experience_years: p.experience_years != null ? String(p.experience_years) : "",
            skills: (p.skills ?? []).join(", "),
            services_offered: (p.services_offered ?? []).join(", "),
            team_size: p.team_size != null ? String(p.team_size) : "",
            business_info: p.business_info ?? "",
            contact_phone: p.contact_phone ?? "",
            contact_email: p.contact_email ?? "",
            profile_avg_rating: p.profile_avg_rating,
            profile_review_count: p.profile_review_count,
            services_avg_rating: p.services_avg_rating,
            services_review_count: p.services_review_count,
            equipment_avg_rating: p.equipment_avg_rating,
            equipment_review_count: p.equipment_review_count,
          });
        } else {
          navigate("/marketplace/profile/create");
        }
      }
    } catch {
      showErrorToast("Could not load profile details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [id]);

  const handleTogglePublic = async () => {
    if (form.is_public_locked) {
      showErrorToast("Your profile visibility has been locked as private by an administrator and cannot be made public.");
      return;
    }
    if (form.is_active === false) {
      showErrorToast("Your account has been deactivated or frozen by an administrator. Profile visibility cannot be changed.");
      return;
    }
    try {
      const newStatus = !form.is_public;
      set("is_public", newStatus);
      await updateMarketplaceProfile({ is_public: newStatus });
      showSuccessToast(newStatus ? "Profile is now public!" : "Profile is now private!");
      fetchProfile();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.is_public || "Failed to update visibility";
      showErrorToast(typeof msg === "string" ? msg : "Failed to update visibility");
      set("is_public", form.is_public);
    }
  };

  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 400, height: 400, facingMode: "user" },
      });
      setCameraStream(stream);
      setShowCamera(true);
      setShowPhotoSourceModal(false);
    } catch (err) {
      showErrorToast("Could not access camera");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById("webcam-video") as HTMLVideoElement;
    const canvas = document.createElement("canvas");
    if (video) {
      canvas.width = video.videoWidth || 400;
      canvas.height = video.videoHeight || 400;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], "profile_photo.jpg", {
                type: "image/jpeg",
              });
              setImageFile(file);
              showSuccessToast("Photo captured!");
            }
            stopCamera();
          },
          "image/jpeg",
          0.9
        );
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    showSuccessToast("Photo selected!");
    setShowPhotoSourceModal(false);
  };

  const addPortfolioItem = () => {
    if (!newPortTitle.trim() || !newPortUrl.trim()) return;
    set("portfolio", [
      ...form.portfolio,
      { title: newPortTitle.trim(), url: newPortUrl.trim() },
    ]);
    setNewPortTitle("");
    setNewPortUrl("");
  };

  const removePortfolioItem = (idx: number) =>
    set("portfolio", form.portfolio.filter((_, i) => i !== idx));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.display_name.trim()) {
      showErrorToast("Display name is required");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("display_name", form.display_name.trim());
      formData.append("bio", form.bio.trim());
      formData.append("city", form.city.trim());
      formData.append("state", form.state.trim());
      formData.append("country", form.country.trim());
      formData.append(
        "is_public",
        String(form.is_public_locked || form.is_active === false ? false : form.is_public)
      );
      formData.append("contact_phone", form.contact_phone.trim());
      formData.append("contact_email", form.contact_email.trim());
      formData.append("profile_type", profileType);
      formData.append("portfolio", JSON.stringify(form.portfolio));

      csvToArray(form.work_categories).forEach((cat) => {
        formData.append("work_categories", cat);
      });

      if (isFreelancer) {
        if (form.professional_type)
          formData.append("professional_type", form.professional_type);
        formData.append("profession", form.profession.trim());
        if (form.experience_years)
          formData.append("experience_years", form.experience_years);
        csvToArray(form.skills).forEach((s) => formData.append("skills", s));
        csvToArray(form.services_offered).forEach((s) =>
          formData.append("services_offered", s)
        );
      }
      if (isStudio) {
        if (form.team_size) formData.append("team_size", form.team_size);
        formData.append("business_info", form.business_info.trim());
        csvToArray(form.services_offered).forEach((s) =>
          formData.append("services_offered", s)
        );
      }

      if (imageFile) {
        formData.append("profile_picture", imageFile);
      } else {
        formData.append("profile_picture", form.profile_picture.trim());
      }

      const updateRes = await updateMarketplaceProfile(formData);
      const updatedProfile = updateRes?.data || updateRes;
      if (updatedProfile) {
        localStorage.setItem("marketplace_profile", JSON.stringify(updatedProfile));
        localStorage.setItem("marketplace_profile_created", "true");
        localStorage.removeItem("marketplace_profile_skipped");
      }
      sendFirebaseEvent('Marketplace_Profile_Updated', 'Marketplace', 'profile_updated', profileType);
      showSuccessToast("Profile updated!");
      setIsEditing(false);
      setImageFile(null);
      fetchProfile();
      window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to save profile";
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_info");
    localStorage.removeItem("marketplace_profile");
    localStorage.removeItem("marketplace_profile_created");
    localStorage.removeItem("marketplace_profile_skipped");
    window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
    showSuccessToast("Signed out successfully");
    navigate("/marketplace");
  };

  if (loading) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <div style={{ width: "100%", position: "relative", minHeight: "100vh", background: "var(--mp-bodyBackground, #0b0c0e)" }}>
      {/* ── PROFILE HERO BANNER WITH profile_bg.webp MATCHING FIGMA ── */}
      <div
        style={{
          width: "100%",
          minHeight: "380px",
          backgroundImage: `linear-gradient(180deg, rgba(15, 17, 21, 0.65) 0%, rgba(15, 17, 21, 0.92) 100%), url(${profileBgImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          padding: "40px 32px",
          boxSizing: "border-box",
          position: "relative"
        }}
      >
        <div
          style={{
            maxWidth: "1320px",
            width: "100%",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: "36px",
            flexWrap: "wrap"
          }}
        >
          {/* Large Profile Avatar (Centered in BG Height) */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            {form.profile_picture ? (
              <img
                src={resolveImage(form.profile_picture)}
                alt={form.display_name}
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "4px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.6)"
                }}
              />
            ) : (
              <div
                style={{
                  width: "140px",
                  height: "140px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.1)",
                  color: "#ffffff",
                  fontSize: "48px",
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "4px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.6)"
                }}
              >
                {form.display_name ? form.display_name.charAt(0).toUpperCase() : <User size={70} />}
              </div>
            )}
          </div>

          {/* Hero Details Header */}
          <div style={{ flex: 1, minWidth: "280px" }}>
            {form.profession && (
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  background: "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "8px"
                }}
              >
                {form.profession}
              </div>
            )}

            <h1
              style={{
                margin: "0 0 16px 0",
                fontSize: "52px",
                fontWeight: 700,
                fontFamily: "'Liberation Serif', serif",
                color: "#ffffff",
                letterSpacing: "-1px"
              }}
            >
              {form.display_name || "Creative Professional"}
            </h1>

            {/* Profile Rating */}
            {(form.profile_avg_rating !== undefined && form.profile_avg_rating > 0) && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "-8px", marginBottom: "8px" }}>
                <div style={{ display: "flex", gap: "2px" }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      size={16}
                      fill={star <= Math.round(form.profile_avg_rating!) ? "#ffb400" : "none"}
                      color={star <= Math.round(form.profile_avg_rating!) ? "#ffb400" : "#d1d5db"}
                    />
                  ))}
                </div>
                <span style={{ fontFamily: "Montserrat, sans-serif", fontSize: "14px", fontWeight: 700, color: "#fff" }}>
                  {Number(form.profile_avg_rating).toFixed(1)}
                </span>
                <span style={{ fontFamily: "Montserrat, sans-serif", fontSize: "12px", color: "rgba(255,255,255,0.6)" }}>
                  ({form.profile_review_count} review{form.profile_review_count !== 1 ? "s" : ""})
                </span>
              </div>
            )}

            {/* Contact details list below profile name matching Figma specifications */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "24px",
                fontFamily: "Montserrat, sans-serif"
              }}
            >
              {(form.city || form.state) && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "10px",
                      background: "var(--mp-iconGrad, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      flexShrink: 0
                    }}
                  >
                    <MapPin size={16} color="#ffffff" />
                  </div>
                  <div>
                    <span style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255, 255, 255, 0.6)", display: "block" }}>LOCATION</span>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#ffffff" }}>{[form.city, form.state, form.country].filter(Boolean).join(", ")}</span>
                  </div>
                </div>
              )}
              {form.contact_email && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "10px",
                      background: "var(--mp-iconGrad, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      flexShrink: 0
                    }}
                  >
                    <Mail size={16} color="#ffffff" />
                  </div>
                  <div>
                    <span style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255, 255, 255, 0.6)", display: "block" }}>EMAIL ADDRESS</span>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#ffffff" }}>{form.contact_email}</span>
                  </div>
                </div>
              )}
              {form.contact_phone && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "10px",
                      background: "var(--mp-iconGrad, linear-gradient(135deg, #FFC951 0%, #C94DAA 100%))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      flexShrink: 0
                    }}
                  >
                    <Phone size={16} color="#ffffff" />
                  </div>
                  <div>
                    <span style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "1px", textTransform: "uppercase", color: "rgba(255, 255, 255, 0.6)", display: "block" }}>PHONE NUMBER</span>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#ffffff" }}>{form.contact_phone}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hero Header Action Buttons */}
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            {!isOtherProfile ? (
              !isEditing ? (
                <>
                  <PublicToggleArea>
                    <ToggleLabel style={{ color: "#ffffff", display: "flex", alignItems: "center", gap: "6px" }}>
                      Public profile
                      {form.is_public_locked && (
                        <span
                          title="Locked as Private by Super Admin"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "3px",
                            padding: "2px 6px",
                            fontSize: "10px",
                            fontWeight: 600,
                            borderRadius: "4px",
                            background: "rgba(239, 68, 68, 0.25)",
                            color: "#fca5a5",
                            border: "1px solid rgba(239, 68, 68, 0.4)",
                          }}
                        >
                          <Lock size={10} /> Locked
                        </span>
                      )}
                    </ToggleLabel>
                    <Toggle
                      type="button"
                      $on={form.is_public}
                      onClick={handleTogglePublic}
                      disabled={form.is_public_locked || form.is_active === false}
                      style={{
                        cursor: form.is_public_locked || form.is_active === false ? "not-allowed" : "pointer",
                        opacity: form.is_public_locked || form.is_active === false ? 0.5 : 1,
                      }}
                      title={
                        form.is_public_locked
                          ? "Locked as Private by Super Admin"
                          : form.is_active === false
                          ? "Account is frozen"
                          : undefined
                      }
                    >
                      <ToggleKnob $on={form.is_public} />
                    </Toggle>
                  </PublicToggleArea>
                  <EditBtn onClick={() => setIsEditing(true)}>
                    <Edit size={14} /> Edit Profile
                  </EditBtn>
                  <OutlineBtn 
                    onClick={handleLogout}
                    style={{
                      borderColor: "rgba(239, 68, 68, 0.4)",
                      background: "rgba(239, 68, 68, 0.12)",
                      color: "#ef4444",
                    }}
                  >
                    <LogOut size={14} /> Sign Out
                  </OutlineBtn>
                </>
              ) : (
                <OutlineBtn onClick={() => setIsEditing(false)}>
                  Cancel
                </OutlineBtn>
              )
            ) : null}
          </div>
        </div>
      </div>

      <ContentWrap style={{ paddingTop: "36px" }}>
        {!isOtherProfile && form.is_active === false && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 18px",
              marginBottom: "24px",
              borderRadius: "10px",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              color: "#fca5a5",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
            <div>
              <strong>Account Frozen / Deactivated:</strong> Your marketplace account has been frozen by an administrator.
              Your profile visibility is locked as private and all listing/booking actions are suspended.
            </div>
          </div>
        )}

        {!isOtherProfile && form.is_active !== false && form.is_public_locked && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 16px",
              marginBottom: "24px",
              borderRadius: "10px",
              background: "rgba(245, 158, 11, 0.12)",
              border: "1px solid rgba(245, 158, 11, 0.35)",
              color: "#fcd34d",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            <Lock size={18} style={{ flexShrink: 0, color: "#f59e0b" }} />
            <div>
              <strong>Profile Visibility Locked as Private:</strong> Your marketplace profile has been made private by an administrator and locked. You cannot republish or make it public.
            </div>
          </div>
        )}

          {!isEditing ? (
            <ProfileViewLayout $hasSidebar={isOtherProfile}>
              {/* MAIN CONTENT (LEFT COLUMN - NARRATIVE, DETAILS, SKILLS, PORTFOLIO) */}
              <RightCard>
                {form.bio && (
                  <ViewSection>
                    <ViewSectionTitle style={{ fontSize: "14px", letterSpacing: "1px", fontWeight: 800, color: "var(--mp-headingColor, #ffffff)", borderBottom: "none", paddingBottom: 0 }}>THE NARRATIVE</ViewSectionTitle>
                    <BioQuote style={{ fontSize: "16px", color: "var(--mp-heroSubColor, rgba(255,255,255,0.85))" }}>"{form.bio}"</BioQuote>
                  </ViewSection>
                )}

                {isFreelancer && (form.profession || form.experience_years) && (
                  <ViewSection>
                    <ViewSectionTitle style={{ fontSize: "14px", letterSpacing: "1px", fontWeight: 800, color: "var(--mp-headingColor, #ffffff)", borderBottom: "none", paddingBottom: 0 }}>PROFESSIONAL PROFILE</ViewSectionTitle>
                    <DetailsGrid>
                      {form.profession && (
                        <DetailGridCell>
                          <Award size={16} color="#c94daa" />
                          <div>
                            <strong>Profession</strong>
                            <span>{form.profession}</span>
                          </div>
                        </DetailGridCell>
                      )}
                      {form.experience_years && (
                        <DetailGridCell>
                          <Briefcase size={16} color="#c94daa" />
                          <div>
                            <strong>Experience</strong>
                            <span>{form.experience_years} Years</span>
                          </div>
                        </DetailGridCell>
                      )}
                    </DetailsGrid>
                  </ViewSection>
                )}

                {isStudio && (form.team_size || form.business_info) && (
                  <ViewSection>
                    <ViewSectionTitle style={{ fontSize: "14px", letterSpacing: "1px", fontWeight: 800, color: "var(--mp-headingColor, #ffffff)", borderBottom: "none", paddingBottom: 0 }}>STUDIO PROFILE</ViewSectionTitle>
                    <DetailsGrid>
                      {form.team_size && (
                        <DetailGridCell>
                          <Users size={16} color="#c94daa" />
                          <div>
                            <strong>Team Size</strong>
                            <span>{form.team_size} members</span>
                          </div>
                        </DetailGridCell>
                      )}
                      {form.business_info && (
                        <DetailGridCell style={{ gridColumn: "1 / -1" }}>
                          <Info size={16} color="#c94daa" />
                          <div>
                            <strong>Business Info</strong>
                            <span>{form.business_info}</span>
                          </div>
                        </DetailGridCell>
                      )}
                    </DetailsGrid>
                  </ViewSection>
                )}

                {(form.skills || form.services_offered) && (
                  <ViewSection>
                    <ViewSectionTitle style={{ fontSize: "14px", letterSpacing: "1px", fontWeight: 800, color: "var(--mp-headingColor, #ffffff)", borderBottom: "none", paddingBottom: 0 }}>
                      {isFreelancer ? "SKILLS & SPECIALTIES" : "SERVICES OFFERED"}
                    </ViewSectionTitle>
                    <TagsWrapper>
                      {isFreelancer &&
                        csvToArray(form.skills).map((s) => (
                          <TagChip key={s}>{s}</TagChip>
                        ))}
                      {csvToArray(form.services_offered).map((s) => (
                        <TagChip key={s} style={{ background: "rgba(255, 201, 81, 0.12)", color: "#b7791f" }}>
                          {s}
                        </TagChip>
                      ))}
                    </TagsWrapper>
                  </ViewSection>
                )}

                {form.portfolio.length > 0 && (
                  <ViewSection>
                    <ViewSectionTitle style={{ fontSize: "14px", letterSpacing: "1px", fontWeight: 800, color: "var(--mp-headingColor, #ffffff)", borderBottom: "none", paddingBottom: 0 }}>PORTFOLIO & CREDITS</ViewSectionTitle>
                    <PortfolioGrid>
                      {form.portfolio.map((item, idx) => (
                        <PortfolioLinkCard
                          key={idx}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ImageIcon size={18} color="#c94daa" />
                          <span>{item.title}</span>
                          <ChevronRight size={14} style={{ marginLeft: "auto" }} />
                        </PortfolioLinkCard>
                      ))}
                    </PortfolioGrid>
                  </ViewSection>
                )}

                {/* Unified Reviews Section */}
                <ViewSection style={{ marginTop: "16px" }}>
                  <ViewSectionTitle style={{ fontSize: "14px", letterSpacing: "1px", fontWeight: 800, color: "var(--mp-headingColor, #ffffff)", borderBottom: "none", paddingBottom: 0 }}>
                    REVIEWS
                  </ViewSectionTitle>
                  <MarketplaceReviewsSection
                    mode="profile"
                    targetId={isOtherProfile ? id! : ownProfile?.id || 0}
                    avgRating={form.profile_avg_rating}
                    reviewCount={form.profile_review_count}
                    equipmentAvgRating={form.equipment_avg_rating}
                    equipmentReviewCount={form.equipment_review_count}
                    servicesAvgRating={form.services_avg_rating}
                    servicesReviewCount={form.services_review_count}
                    isOwner={!isOtherProfile}
                  />
                </ViewSection>
              </RightCard>

              {/* SIDEBAR (RIGHT COLUMN - BOOKING ACTIONS MATCHING FIGMA) */}
              {isOtherProfile && (
                <LeftCard>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", marginTop: "8px" }}>
                    <button
                      onClick={() => {
                        if (form.contact_phone) {
                          window.location.href = `tel:${form.contact_phone}`;
                        } else {
                          showSuccessToast(`Booking request initiated for ${form.display_name}`);
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "44px",
                        borderRadius: "22px",
                        background: "var(--mp-heroBtnBg, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%))",
                        color: "white",
                        border: "none",
                        fontWeight: 800,
                        fontSize: "13px",
                        letterSpacing: "0.8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.15)"
                      }}
                    >
                      <Calendar size={16} /> REQUEST BOOKING
                    </button>

                    <button
                      onClick={() => {
                        if (form.contact_email) {
                          window.location.href = `mailto:${form.contact_email}`;
                        } else {
                          showSuccessToast(`Message sent to ${form.display_name}`);
                        }
                      }}
                      style={{
                        width: "100%",
                        height: "44px",
                        borderRadius: "22px",
                        background: "transparent",
                        color: "var(--mp-headingColor, #ffffff)",
                        border: "1.5px solid var(--mp-border, rgba(255,255,255,0.2))",
                        fontWeight: 800,
                        fontSize: "13px",
                        letterSpacing: "0.8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px"
                      }}
                    >
                      <MessageCircle size={16} /> MESSAGE CREATIVE
                    </button>
                  </div>
                </LeftCard>
              )}
            </ProfileViewLayout>
          ) : (
            <FormCard onSubmit={handleSubmit}>
              <Section>
                <SectionLabel>Basic Information</SectionLabel>
                <TwoCol>
                  <FormGroup>
                    <Label>Display Name *</Label>
                    <Input
                      value={form.display_name}
                      onChange={(e) => set("display_name", e.target.value)}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Profile Picture</Label>
                    <ImageUploadAreaEdit>
                      {imageFile || form.profile_picture ? (
                        <ImagePreviewEdit>
                          <PreviewImgEdit
                            src={
                              imageFile
                                ? URL.createObjectURL(imageFile)
                                : form.profile_picture
                            }
                            alt="Profile"
                          />
                          <RemoveImgBtnEdit
                            type="button"
                            onClick={() => {
                              setImageFile(null);
                              set("profile_picture", "");
                            }}
                          >
                            <X size={14} />
                          </RemoveImgBtnEdit>
                        </ImagePreviewEdit>
                      ) : (
                        <UploadLabelEdit
                          type="button"
                          onClick={() => setShowPhotoSourceModal(true)}
                        >
                          <Upload size={18} color="var(--mp-heroSubColor, #c94daa)" />
                          <span>Choose Photo</span>
                        </UploadLabelEdit>
                      )}
                    </ImageUploadAreaEdit>
                  </FormGroup>
                </TwoCol>

                <FormGroup>
                  <Label>Bio / About Me</Label>
                  <Textarea
                    value={form.bio}
                    onChange={(e) => set("bio", e.target.value)}
                    placeholder="Tell clients about yourself, your style, specializations, etc."
                    rows={4}
                  />
                </FormGroup>
              </Section>

              <Section>
                <SectionLabel>Contact Details</SectionLabel>
                <TwoCol>
                  <FormGroup>
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={form.contact_email}
                      onChange={(e) => set("contact_email", e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Contact Phone</Label>
                    <Input
                      value={form.contact_phone}
                      onChange={(e) => set("contact_phone", e.target.value)}
                    />
                  </FormGroup>
                </TwoCol>
              </Section>

              <Section>
                <SectionLabel>Location</SectionLabel>
                <ThreeCol>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      value={form.city}
                      onChange={(e) => set("city", e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>State</Label>
                    <Input
                      value={form.state}
                      onChange={(e) => set("state", e.target.value)}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Country</Label>
                    <Input
                      value={form.country}
                      onChange={(e) => set("country", e.target.value)}
                    />
                  </FormGroup>
                </ThreeCol>
              </Section>

              {isFreelancer && (
                <Section>
                  <SectionLabel>Professional Details</SectionLabel>
                  <TwoCol>
                    <FormGroup>
                      <Label>Professional Type</Label>
                      <Select
                        value={form.professional_type}
                        onChange={(e) =>
                          set(
                            "professional_type",
                            e.target.value as ProfessionalType | ""
                          )
                        }
                      >
                        <option value="">Select type</option>
                        <option value="photographer">Photographer</option>
                        <option value="videographer">Videographer</option>
                        <option value="both">Both</option>
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Profession / Title</Label>
                      <Input
                        value={form.profession}
                        onChange={(e) => set("profession", e.target.value)}
                        placeholder="e.g. Wedding Photographer"
                      />
                    </FormGroup>
                  </TwoCol>
                  <TwoCol>
                    <FormGroup>
                      <Label>Years of Experience</Label>
                      <Input
                        type="number"
                        value={form.experience_years}
                        onChange={(e) => set("experience_years", e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Skills (comma-separated)</Label>
                      <Input
                        value={form.skills}
                        onChange={(e) => set("skills", e.target.value)}
                        placeholder="e.g. portrait, lighting, editing"
                      />
                    </FormGroup>
                  </TwoCol>
                </Section>
              )}

              {isStudio && (
                <Section>
                  <SectionLabel>Studio details</SectionLabel>
                  <TwoCol>
                    <FormGroup>
                      <Label>Team Size</Label>
                      <Input
                        type="number"
                        value={form.team_size}
                        onChange={(e) => set("team_size", e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Business Info</Label>
                      <Input
                        value={form.business_info}
                        onChange={(e) => set("business_info", e.target.value)}
                        placeholder="e.g. Established in 2018, fully equipped studio"
                      />
                    </FormGroup>
                  </TwoCol>
                </Section>
              )}

              <Section>
                <SectionLabel>Skills & Categories</SectionLabel>
                <TwoCol>
                  <FormGroup>
                    <Label>Services Offered (comma-separated)</Label>
                    <Input
                      value={form.services_offered}
                      onChange={(e) => set("services_offered", e.target.value)}
                      placeholder="e.g. pre-wedding shoot, drone shots"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Work Categories (comma-separated)</Label>
                    <Input
                      value={form.work_categories}
                      onChange={(e) => set("work_categories", e.target.value)}
                      placeholder="e.g. wedding, corporate, events"
                    />
                  </FormGroup>
                </TwoCol>
              </Section>

              <Section>
                <SectionLabel>Portfolio Work</SectionLabel>
                {form.portfolio.length > 0 && (
                  <PortfolioList>
                    {form.portfolio.map((item, idx) => (
                      <PortfolioItemRow key={idx}>
                        <ImageIcon size={15} color="#c94daa" />
                        <span style={{ fontSize: "13px", fontWeight: 700 }}>
                          {item.title}
                        </span>
                        <span style={{ fontSize: "11px", opacity: 0.6, marginLeft: "10px" }}>
                          ({item.url})
                        </span>
                        <RemovePortBtn
                          type="button"
                          onClick={() => removePortfolioItem(idx)}
                        >
                          <X size={14} />
                        </RemovePortBtn>
                      </PortfolioItemRow>
                    ))}
                  </PortfolioList>
                )}
                <PortfolioAddBox>
                  <Input
                    value={newPortTitle}
                    onChange={(e) => setNewPortTitle(e.target.value)}
                    placeholder="Title, e.g. Pre-Wedding Portfolio"
                    style={{ flex: 1 }}
                  />
                  <Input
                    value={newPortUrl}
                    onChange={(e) => setNewPortUrl(e.target.value)}
                    placeholder="URL link"
                    style={{ flex: 2 }}
                  />
                  <AddPortBtn type="button" onClick={addPortfolioItem}>
                    Add Link
                  </AddPortBtn>
                </PortfolioAddBox>
              </Section>

              <FormFooter>
                <CancelBtn type="button" onClick={() => setIsEditing(false)}>
                  Cancel
                </CancelBtn>
                <SubmitBtn type="submit" disabled={submitting}>
                  <Save size={15} />
                  {submitting ? "Saving…" : "Save Changes"}
                </SubmitBtn>
              </FormFooter>
            </FormCard>
          )}

          {showPhotoSourceModal && (
            <PhotoModalOverlay onClick={() => setShowPhotoSourceModal(false)}>
              <PhotoModalContent onClick={(e) => e.stopPropagation()}>
                <CloseModalBtn onClick={() => setShowPhotoSourceModal(false)}>
                  <X size={16} />
                </CloseModalBtn>
                <ModalTitle>Select Profile Photo</ModalTitle>
                <OptionButton onClick={startCamera}>
                  <Camera size={16} color="#c94daa" /> Take Photo (Camera)
                </OptionButton>
                <OptionButton
                  onClick={() => document.getElementById("gallery-upload")?.click()}
                >
                  <ImageIcon size={16} color="#ffc951" /> Choose from Gallery
                </OptionButton>
                <input
                  id="gallery-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
              </PhotoModalContent>
            </PhotoModalOverlay>
          )}

          {showCamera && (
            <PhotoModalOverlay>
              <PhotoModalContent style={{ maxWidth: "380px" }}>
                <CloseModalBtn onClick={stopCamera}>
                  <X size={16} />
                </CloseModalBtn>
                <ModalTitle>Camera Preview</ModalTitle>
                <CameraContainer>
                  <CameraVideo
                    id="webcam-video"
                    autoPlay
                    playsInline
                    ref={(el) => {
                      if (el && cameraStream) el.srcObject = cameraStream;
                    }}
                  />
                </CameraContainer>
                <CameraControls>
                  <CaptureBtn type="button" onClick={capturePhoto}>
                    Capture Photo
                  </CaptureBtn>
                  <OptionButton
                    type="button"
                    onClick={stopCamera}
                    style={{ background: "transparent" }}
                  >
                    Cancel
                  </OptionButton>
                </CameraControls>
              </PhotoModalContent>
            </PhotoModalOverlay>
          )}
        </ContentWrap>
      </div>
  );
};

export default MarketplaceMyProfilePage;
